import type { Price } from '@core/products-types';
import { AddonType } from '@core/subscription-types';

const addonTypePriceKeyMap: Partial<Record<AddonType, keyof Price>> = {
  [AddonType.Sso]: 'ssoPrice',
  [AddonType.Texting]: 'textingPrice',
  [AddonType.Kiwi]: 'kiwihrPlusPrice',
};

type ActiveAddon = {
  type: AddonType;
  quantity: number;
};

export const get2024ProductCosts = (
  prices: Price[],
  addons: ActiveAddon[],
  planQuantity: number,
  billingPeriod: number,
) => {
  const pricesForPeriod = prices
    .filter(price => price.billingPeriodMonths === billingPeriod)
    .sort((a, b) => a.fteRangeStart - b.fteRangeStart);

  if (!pricesForPeriod.length) {
    throw new Error(`Missing prices for billingPeriod: ${billingPeriod}`);
  }

  const priceForQuantity = pricesForPeriod.find(price =>
    price.fteRangeEnd
      ? price.fteRangeStart <= planQuantity && price.fteRangeEnd >= planQuantity
      : price.fteRangeStart <= planQuantity,
  )!;

  const priceForAddon = (prices: Price[], addon: ActiveAddon) => {
    const addonPrice = prices.find(price =>
      price.fteRangeEnd
        ? price.fteRangeStart <= addon.quantity && price.fteRangeEnd >= addon.quantity
        : price.fteRangeStart <= addon.quantity,
    );
    if (!addonPrice) return 0;
    const priceKey = addonTypePriceKeyMap[addon.type]!; // addons were already filtered by addonTypePriceKeyMap[addon.type] existence
    return addonPrice[priceKey] || 0;
  };

  const addonsCost = addons
    .filter(addon => !!addonTypePriceKeyMap[addon.type])
    .map(addon => {
      if (addon.type === AddonType.Sso) {
        return priceForAddon(pricesForPeriod, addon);
      } else {
        return priceForAddon(pricesForPeriod, addon) * addon.quantity;
      }
    })
    .reduce((value, sum) => value + sum, 0);

  return (priceForQuantity.basePrice + addonsCost) / 100;
};
